import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { ExpandMore } from '@mui/icons-material';
import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  useTheme,
} from '@mui/material';
import React from 'react';

export interface DropdownOption {
  value: string | number;
  label: string;
}

interface DropdownProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: DropdownOption[];
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  'data-testid'?: string;
}

export const Dropdown: React.FC<DropdownProps> = ({
  value,
  onChange,
  options,
  error,
  placeholder,
  disabled = false,
  fullWidth = true,
  'data-testid': dataTestId,
}) => {
  const theme = useTheme<LensTheme>();

  return (
    <FormControl error={!!error} fullWidth={fullWidth} disabled={disabled}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value as string | number)}
        displayEmpty={!!placeholder}
        IconComponent={ExpandMore}
        data-testid={dataTestId}
        renderValue={(selected) => {
          if (!selected) {
            return (
              <span
                style={{ color: theme.tokens.semantic.color.type.muted.value }}
              >
                {placeholder}
              </span>
            );
          }
          return (
            options.find((o) => o.value === selected)?.label ??
            (selected as string)
          );
        }}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
            sx={{
              color: theme.tokens.semantic.color.type.default.value,
              borderBottom: `1px solid ${theme.tokens.semantic.color.ui.divider.default.value}`,
              '&:hover': {
                backgroundColor:
                  theme.tokens.semantic.color.action.secondary.hoverFill.value,
              },
              '&.Mui-selected:hover': {
                backgroundColor:
                  theme.tokens.semantic.color.action.secondary.hoverFill.value,
              },
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};
